import { AnswerEditorRendererContainer } from '@/features/jobs/questions/components/AnswerEditorRendererContainer'
import { QuestionStepViewModel } from '@/features/jobs/drafting/IntakeQuestionsViewModel'
import { LegendSelectorViewModel } from '@/features/jobs/drafting/LegendSelectorViewModel'
import { type StepAccessor } from '@/features/jobs/drafting/OutlineViewModel'
import { PreviousProviderSelectionViewModel } from '@/features/jobs/drafting/PreviousProviderSelectionViewModel'
import { TeamSelectorViewModel } from '@/features/jobs/drafting/TeamSelectorViewModel'
import { WorkspaceSelectorViewModel } from '@/features/jobs/drafting/WorkspaceSelectorViewModel'
import { NameInputViewModel } from '@/features/jobs/drafting/NameInputViewModel'
import { OwnerSelectorViewModel } from '@/features/jobs/drafting/OwnerSelectorViewModel'
import { RequestProviderSelectorViewModel } from '@/features/jobs/drafting/RequestProviderSelectorViewModel'
import { PreviousProviderSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/PreviousProviderSelectorContainer'
import { CompactLegendSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactLegendSelectorContainer'
import { CompactTeamSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactTeamSelectorContainer'
import { CompactWorkspaceSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactWorkspaceSelectorContainer'
import { CompactNameInputContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactNameInputContainer'
import { CompactOwnerSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactOwnerSelectorContainer'
import { CompactRequestProviderSelectorContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/CompactRequestProviderSelectorContainer'

/**
 * Options for {@link renderStepContent}.
 */
export interface RenderStepContentOptions {
  /**
   * Whether the legend can no longer be changed.
   */
  isLegendDisabled?: boolean

  /**
   * Whether unanswered questions are flagged.
   */
  isValidationShown?: boolean
}

/**
 * Renders the editor of a draft step, or nothing for steps without one.
 *
 * @param stepAccessor
 * @param options
 */
export function renderStepContent(
  stepAccessor: StepAccessor,
  { isLegendDisabled, isValidationShown }: RenderStepContentOptions = {},
) {
  if (stepAccessor instanceof NameInputViewModel) {
    return <CompactNameInputContainer vm={stepAccessor} />
  }

  if (stepAccessor instanceof WorkspaceSelectorViewModel) {
    return <CompactWorkspaceSelectorContainer vm={stepAccessor} />
  }

  if (stepAccessor instanceof OwnerSelectorViewModel) {
    return <CompactOwnerSelectorContainer vm={stepAccessor} />
  }

  if (stepAccessor instanceof TeamSelectorViewModel) {
    return <CompactTeamSelectorContainer vm={stepAccessor} />
  }

  if (stepAccessor instanceof RequestProviderSelectorViewModel) {
    return <CompactRequestProviderSelectorContainer vm={stepAccessor} />
  }

  if (stepAccessor instanceof LegendSelectorViewModel) {
    return (
      <CompactLegendSelectorContainer
        vm={stepAccessor}
        isDisabled={isLegendDisabled}
      />
    )
  }

  if (stepAccessor instanceof QuestionStepViewModel) {
    return (
      <AnswerEditorRendererContainer
        questionId={stepAccessor.question.id}
        editorVm={stepAccessor.question.editor}
        isDisabled={stepAccessor.disabled}
        focus$={stepAccessor.step.becomeActive$}
        onFocus={stepAccessor.step.onBecameActive}
        isNotValid={isValidationShown && !stepAccessor.step.isCompleted}
      />
    )
  }

  if (stepAccessor instanceof PreviousProviderSelectionViewModel) {
    return <PreviousProviderSelectorContainer vm={stepAccessor} />
  }

  return null
}
